/**
 * API Client
 * 
 * Provides utilities for making API requests
 */

import { getApiConfig, getApiUrl } from './ApiConfig';

/**
 * Request options for the API client
 */
export interface ApiRequestOptions extends RequestInit {
  /** Query parameters to add to the URL */
  params?: Record<string, string | number | boolean | undefined | null>;
  
  /** Timeout in milliseconds */
  timeout?: number;
}

/**
 * Response from the API client
 */
export interface ApiResponse<T = any> {
  /** Response data */
  data: T;
  
  /** Response status code */
  status: number;
  
  /** Response headers */
  headers: Headers;
  
  /** Original response object */
  response: Response;
}

/**
 * Error from the API client
 */
export class ApiError extends Error {
  /** Response status code */
  status?: number;
  
  /** Response data */
  data?: any;
  
  /** Original error */
  originalError?: Error;
  
  /** Original response */
  response?: Response;
  
  constructor(message: string, options?: {
    status?: number;
    data?: any;
    originalError?: Error;
    response?: Response;
  }) {
    super(message);
    this.name = 'ApiError';
    
    if (options) {
      this.status = options.status;
      this.data = options.data;
      this.originalError = options.originalError;
      this.response = options.response;
    }
  }
}

/**
 * Makes an API request
 * 
 * @param path Path to the API endpoint
 * @param options Request options
 * @returns Promise that resolves to the API response
 */
export async function request<T = any>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const config = getApiConfig();
  const url = buildUrl(path, options.params);
  
  // Merge default headers with request headers
  const headers = {
    ...config.defaultHeaders,
    ...options.headers
  };
  
  // Set up the fetch options
  const fetchOptions: RequestInit = {
    ...options,
    headers
  };
  
  // Set up timeout
  const timeoutDuration = options.timeout || config.timeout;
  
  try {
    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new ApiError(`Request timed out after ${timeoutDuration}ms`, {
          status: 408
        }));
      }, timeoutDuration);
    });
    
    // Make the request with timeout
    const response = await Promise.race([
      fetch(url, fetchOptions),
      timeoutPromise
    ]) as Response;
    
    // Check if the response is ok (status in the range 200-299)
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = null;
      }
      
      throw new ApiError(`Request failed with status ${response.status}`, {
        status: response.status,
        data: errorData,
        response
      });
    }
    
    // Parse the response
    const data = await parseResponse<T>(response);
    
    return {
      data,
      status: response.status,
      headers: response.headers,
      response
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      { originalError: error instanceof Error ? error : undefined }
    );
  }
}

/**
 * Parses a response based on content type
 * 
 * @param response The response to parse
 * @returns The parsed response data
 */
async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  
  if (contentType?.includes('application/json')) {
    return await response.json();
  } else if (contentType?.includes('text/')) {
    return await response.text() as unknown as T;
  } else if (contentType?.includes('application/octet-stream')) {
    return await response.blob() as unknown as T;
  }
  
  // Default to JSON
  try {
    return await response.json();
  } catch {
    // If JSON parsing fails, return the text
    return await response.text() as unknown as T;
  }
}

/**
 * Builds a URL with query parameters
 * 
 * @param url Base URL
 * @param params Query parameters
 * @returns URL with query parameters
 */
function buildUrl(
  url: string,
  params?: Record<string, string | number | boolean | undefined | null>
): string {
  const fullUrl = getApiUrl(url);
  
  if (!params) {
    return fullUrl;
  }
  
  const queryParams = new URLSearchParams();
  
  // Add params that are not undefined or null
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  
  const queryString = queryParams.toString();
  
  if (!queryString) {
    return fullUrl;
  }
  
  // Add the query string to the URL
  return `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}${queryString}`;
}

/**
 * Shortcut for GET requests
 * 
 * @param path Path to the API endpoint
 * @param options Request options
 * @returns Promise that resolves to the API response
 */
export async function get<T = any>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  return request<T>(path, {
    ...options,
    method: 'GET'
  });
}

/**
 * Shortcut for POST requests
 * 
 * @param path Path to the API endpoint
 * @param data Data to send in the request body
 * @param options Request options
 * @returns Promise that resolves to the API response
 */
export async function post<T = any>(
  path: string,
  data: any,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  return request<T>(path, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Shortcut for PUT requests
 * 
 * @param path Path to the API endpoint
 * @param data Data to send in the request body
 * @param options Request options
 * @returns Promise that resolves to the API response
 */
export async function put<T = any>(
  path: string,
  data: any,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  return request<T>(path, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

/**
 * Shortcut for PATCH requests
 * 
 * @param path Path to the API endpoint
 * @param data Data to send in the request body
 * @param options Request options
 * @returns Promise that resolves to the API response
 */
export async function patch<T = any>(
  path: string,
  data: any,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  return request<T>(path, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

/**
 * Shortcut for DELETE requests
 * 
 * @param path Path to the API endpoint
 * @param options Request options
 * @returns Promise that resolves to the API response
 */
export async function del<T = any>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  return request<T>(path, {
    ...options,
    method: 'DELETE'
  });
}